import React from "react";
import { cn } from "@/lib/utils";

type TableProps = {
  /** CSS `grid-template-columns` for every row (header + body share it). */
  columns: string;
  /** Row min-width before the grid overflow-scrolls / card-collapses. */
  minWidth?: number | string;
  /** Card-collapse at 560px instead of 700px — for tables in a narrow column. */
  tight?: boolean;
  /** Draw the proto `.dt` frame (1px border + 10px radius) around the table. */
  framed?: boolean;
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
};

/** Data table — a styled CSS grid (NOT a `<table>`; partial table ARIA is worse
 *  than none) with header/body/cell slots, hover + selected rows, and a built-in
 *  mobile card-collapse (each `data-label` cell becomes a stacked label/value
 *  row). The column template lives on the instance; screen-specific cell content
 *  keeps its own classes. Supersedes `.vb-dt` / `.vb-dt--analyses` / `.src-table`. */
export function Table({ columns, minWidth, tight, framed, ariaLabel, className, children }: TableProps) {
  const style = {
    "--vb-table-cols": columns,
    ...(minWidth != null
      ? { "--vb-table-min": typeof minWidth === "number" ? `${minWidth}px` : minWidth }
      : {}),
  } as React.CSSProperties;
  return (
    <div className={cn("vb-table-outer", framed && "vb-table-outer--framed")}>
      <div
        className={cn("vb-table", tight && "vb-table--tight", className)}
        style={style}
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>
  );
}

/** Header row. Children are the column labels (plain spans). */
export function TableHead({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("vb-table__row vb-table__row--head", className)}>{children}</div>;
}

type RowProps = {
  children: React.ReactNode;
  /** Clickable/hoverable row (still needs a focusable control inside for
   *  keyboard users — the row click is a mouse convenience). */
  interactive?: boolean;
  selected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onClick" | "className">;

export function TableRow({ children, interactive, selected, onClick, className, ...rest }: RowProps) {
  return (
    <div
      className={cn(
        "vb-table__row",
        interactive && "vb-table__row--interactive",
        selected && "vb-table__row--selected",
        className,
      )}
      onClick={onClick}
      {...rest}
    >
      {children}
    </div>
  );
}

type CellProps = {
  children: React.ReactNode;
  /** Mobile card-collapse label. Cells without one don't restack (e.g. the
   *  primary name cell becomes the card title). */
  label?: string;
  className?: string;
};

/** Optional sugar for a body cell — a `<span>` carrying the `data-label`. Cells
 *  with bespoke content/classes can just render a `<span data-label>` directly. */
export function TableCell({ children, label, className }: CellProps) {
  return (
    <span className={className} data-label={label}>
      {children}
    </span>
  );
}

export default Table;
